import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import client from "@/lib/apiClient";
import { useLang } from "@/i18n";
import { useProperty } from "@/context/PropertyContext";
import { SectionHeader, Loader, Lu, PRIORITY_META } from "@/components/shared";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

const STATUS_META = {
  open: { color: "#F59E0B", key: "open" },
  in_progress: { color: "#3B82F6", key: "inProgress" },
  resolved: { color: "#10B981", key: "resolved" },
};

export default function Tickets() {
  const { t, lang } = useLang();
  const { selectedId } = useProperty();
  const [tickets, setTickets] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => client.get(`/properties/${selectedId}/tickets`).then((r) => setTickets(r.data));
  useEffect(() => { if (selectedId) { setTickets(null); load(); } }, [selectedId]);

  const advance = async (tk) => {
    const next = tk.status === "open" ? "in_progress" : "resolved";
    await client.post(`/properties/${selectedId}/tickets/${tk.id}`, { status: next });
    setTickets((ts) => ts.map((x) => (x.id === tk.id ? { ...x, status: next } : x)));
    toast.success(t(STATUS_META[next].key));
  };

  if (!tickets) return <Loader />;
  const filtered = filter === "all" ? tickets : tickets.filter((x) => x.status === filter);

  return (
    <div>
      <SectionHeader icon="ticket" title={t("tickets")} subtitle={t("liveData")} />
      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList className="bg-foreground/5 border border-border">
          {["all", "open", "in_progress", "resolved"].map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`tk-filter-${s}`}
              className="data-[state=active]:bg-primary data-[state=active]:shadow-md data-[state=active]:text-primary-foreground text-foreground/80">
              {s === "all" ? t("all") : t(STATUS_META[s].key)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="space-y-3">
        {filtered.map((tk, i) => {
          const sm = STATUS_META[tk.status];
          const pm = PRIORITY_META[tk.priority];
          return (
            <motion.div key={tk.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
              data-testid={`ticket-${tk.id}`} className="md-card p-4 flex flex-wrap items-center gap-4">
              <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${pm.color}1a` }}>
                <Lu name={tk.category === "housekeeping" ? "brush-cleaning" : "wrench"} className="w-4.5 h-4.5" style={{ color: pm.color, width: 18, height: 18 }} />
              </span>
              <div className="flex-1 min-w-[180px]">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-foreground">{tk[lang === "ar" ? "title_ar" : "title_en"]}</p>
                  {tk.auto_generated && <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-semibold">AUTO</span>}
                </div>
                <p className="text-xs text-muted-foreground/70 mt-0.5">{t("room")} {tk.room_number} · {tk.assignee}</p>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-full" style={{ color: pm.color, background: `${pm.color}1a` }}>{t(tk.priority)}</span>
              <span className="text-xs px-2.5 py-1 rounded-full flex items-center gap-1.5" style={{ color: sm.color, background: `${sm.color}1a` }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: sm.color }} />{t(sm.key)}
              </span>
              {tk.status !== "resolved" && (
                <Button size="sm" data-testid={`advance-${tk.id}`} onClick={() => advance(tk)}
                  className="h-8 bg-foreground/5 hover:bg-foreground/10 border border-border text-foreground/80">
                  {tk.status === "open" ? <><Lu name="play" className="w-3.5 h-3.5 ltr:mr-1.5 rtl:ml-1.5" />{t("inProgress")}</> : <><Lu name="check" className="w-3.5 h-3.5 ltr:mr-1.5 rtl:ml-1.5" />{t("resolve")}</>}
                </Button>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
